import {Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {AlertActions, AlertModal} from 'presentation/components';
import {
	FeaturesEnum,
	HomeEnum,
	QueryParamsEnum,
	QueryParamsForTaggingEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {BookingTypeEnum, FlowEnum, TELEMEDICINE_INFO} from '../../constants';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';

import {DuplicityBookedCard} from '../DuplicityBookedCard';
import {CUSTOM_TEXTS} from './constants';
import {DuplicityDialogProps} from './types';
import {useGetHealthDistrictsQuery} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthDistrictSelection/queries/useGetHealthDistrictsQuery';
import {useNavigate} from 'react-router-dom';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {getFlowTypeByBookingType} from 'presentation/utils';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {ONLY_TELEMEDICINE_SPECIALTIES_CODE} from 'presentation/modules/booking/shared/constants/onlyTelemedicineSpecialties';

const {goBack, goToHome} = translations['pt-br'].commons;

export function DuplicityDialog({
	flowType,
	open,
	setOpen,
	scheduledBooking,
	isScheduleScript,
	handleBack,
}: DuplicityDialogProps) {
	const navigate = useNavigate();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isTelehealth = flowType === FlowEnum.TELEHEALTH;

	const {isMobile} = useMediaQueries();
	const {colors} = useTheme();
	const {setSearchParams} = useQueryParams();
	const {isEnabled: isOnlyTelemedicineSpecialtyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SPECIALTY_ONLY_TELEMEDICINE_CHIP,
	);

	const shouldRedirectToTelemedicineFlow =
		isHealthFlow &&
		ONLY_TELEMEDICINE_SPECIALTIES_CODE.includes(bookingData.specialtyCode) &&
		isOnlyTelemedicineSpecialtyEnabled;

	const {isManualLocation} = bookingData;
	const districtParams: GetHealthDistrictsParams = {
		stateCode: (bookingData.ufCode ?? '').toUpperCase(),
		locationName: (bookingData.cityName ?? '').toUpperCase(),
		locationType:
			bookingData.typeLocalName === TypeLocalNameEnum.COORDENADAS
				? TypeLocalNameEnum.CIDADE
				: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(bookingData.specialtyCode ?? 0),
	};

	const scheduledBookingType = getFlowTypeByBookingType(scheduledBooking.bookingType);

	const {isFetching: isDistrictListFetching, refetch: refetchDistrictList} =
		useGetHealthDistrictsQuery({
			params: districtParams,
			enabled: false,
		});

	const disabledSwapSpecialtyButton = isHealthFlow && isDistrictListFetching && isManualLocation;

	function handleOnBack() {
		if (isScheduleScript) {
			navigate(HomeEnum.HOME_PAGE, {replace: true});
		}
		if (isScheduleScript && handleBack) {
			handleBack();
		}
		setOpen(false);
	}

	async function handleSwapSpecialty() {
		const {DISTRICT_STEP, UNIT_STEP, PROFESSIONAL_STEP} = getStepPosition({
			flowType,
			isManualLocation,
		});

		if (shouldRedirectToTelemedicineFlow) {
			const {PROFESSIONAL_STEP: TELEHEALTH_PROFISSIONAL_STEP} = getTelehealthStepPosition();
			setBookingData(prev => ({
				...prev,
				unitCode: TELEMEDICINE_INFO.code,
				unitName: TELEMEDICINE_INFO.name,
				unitAddress: '',
				ufCode: TELEMEDICINE_INFO.ufCode,
				ufName: TELEMEDICINE_INFO.ufName,
				cityName: TELEMEDICINE_INFO.cityName,
				bookingRedirect: true,
				bookingType: BookingTypeEnum.TELEHEALTH,
				subSpecialtyCode:
					!scheduledBooking.subSpecialtyCode || scheduledBooking.subSpecialtyCode === '0'
						? ''
						: scheduledBooking.subSpecialtyCode,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
				isRebooking: true,
				bookingNumber: scheduledBooking.bookingNumber,
				typeLocalName: TypeLocalNameEnum.CIDADE,
				isManualLocation: true,
				initialBookingLocationData: {
					coordinates: prev.coordinates,
					ufCode: prev.ufCode,
					ufName: prev.ufName,
					cityName: prev.cityName,
					typeLocalName: prev.typeLocalName || TypeLocalNameEnum.CIDADE,
					bookingType: BookingTypeEnum.HEALTH,
				},
			}));
			setActiveBookingFlowStep(TELEHEALTH_PROFISSIONAL_STEP);
			navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
			setSearchParams(prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				return prev;
			});

			return;
		}

		let areDistrictsEmpty = false;
		if (isHealthFlow && isManualLocation) {
			const {
				data: districtList,
				error: districtListError,
				isError: isDistrictListError,
			} = await refetchDistrictList();

			const districtListHasUnprocessableError =
				isDistrictListError &&
				districtListError instanceof APIError &&
				districtListError.statusCode === HttpStatusCode.unprocessable;
			const districtListEmpty = districtList && districtList.length === 0;
			areDistrictsEmpty = Boolean(districtListHasUnprocessableError || districtListEmpty);
		}

		const isHealthWithNoDistrictsAndHasTelehealthAndIsManualLocation =
			isHealthFlow && areDistrictsEmpty && bookingData.hasTelehealth && isManualLocation;
		const isExamOrHealth = isExamFlow || isHealthFlow;

		const {
			specialtyNameSelected,
			specialtyName,
			bookingNumber,
			subSpecialtyCode,
			specialtyCode,
		} = scheduledBooking;

		const bookingStepToRedirect = () => {
			if (isTelehealth) return PROFESSIONAL_STEP;
			if (isHealthWithNoDistrictsAndHasTelehealthAndIsManualLocation) return UNIT_STEP;
			if (isExamOrHealth) {
				return isManualLocation ? DISTRICT_STEP : UNIT_STEP;
			}
			if (isOdontoFlow) return DISTRICT_STEP;
			return PROFESSIONAL_STEP;
		};

		setActiveBookingFlowStep(bookingStepToRedirect());

		setBookingData(prev => ({
			...prev,
			specialtyCode: specialtyCode,
			subSpecialtyCode: !subSpecialtyCode || subSpecialtyCode === '0' ? '' : subSpecialtyCode,
			specialtyName: specialtyNameSelected ?? specialtyName,
			bookingNumber: bookingNumber,
			isRebooking: true,
			isScheduleScript: false,
			districtName: undefined,
			citiesNames: undefined,
			hasGeoDistrictsFilter: false,
			unitCode: isTelehealth ? TELEMEDICINE_INFO.code : prev.unitCode,
			unitName: isTelehealth ? TELEMEDICINE_INFO.name : prev.unitName,
		}));

		if (isTelehealth) {
			navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW, {replace: true});
		}

		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			prev.delete(QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER);
			return prev;
		});
	}

	function handleOnClose() {
		if (isScheduleScript) {
			return;
		}
		setOpen(false);
	}

	return (
		<AlertModal
			disableClose={isScheduleScript}
			data-tag-id={FinishWithoutCompleteFlowEnum.POSSUI_CONSULTA_EXISTENTE_ESPECIALIDADE}
			showIcon={false}
			footerContent={
				<AlertActions
					primaryAction={handleSwapSpecialty}
					secondaryAction={handleOnBack}
					secondaryText={isScheduleScript ? goToHome : goBack}
					primaryText={CUSTOM_TEXTS[scheduledBookingType].headerTitle}
					flexDirection={'column-reverse'}
					secondaryBtnVariant="outlined"
					isLoading={disabledSwapSpecialtyButton}
					isDisabled={disabledSwapSpecialtyButton}
					customStackProps={{bgcolor: colors.brand.primary.white, p: '8px 16px'}}
				/>
			}
			footerProps={{
				sx: {
					p: 0,
				},
			}}
			description={isMobile ? undefined : CUSTOM_TEXTS[scheduledBookingType].description}
			headerTitle={CUSTOM_TEXTS[scheduledBookingType].headerTitle}
			onClose={handleOnClose}
			open={open}
			variant="warning"
			slotProps={{
				paper: {
					style: {
						maxWidth: isMobile ? '100%' : 450,
						borderRadius: isMobile ? 0 : 8,
						margin: isMobile ? 0 : 16,
						backgroundColor: isMobile ? colors.gray['20Percent'] : undefined,
					},
				},
			}}
			fullScreen={isMobile}
			customContentStyles={{
				sx: {
					p: isMobile ? 0 : 3,
				},
			}}
			subDescription={
				<Fragment>
					{isMobile ? (
						<Stack sx={{bgcolor: colors.brand.primary.blue}} p={3}>
							<Typography variant="h6" color={colors.brand.primary.white}>
								{CUSTOM_TEXTS[scheduledBookingType].description}
							</Typography>
						</Stack>
					) : null}
					<Stack px={isMobile ? 3 : 0} width={'100%'}>
						<DuplicityBookedCard
							scheduledBooking={scheduledBooking}
							maxWidth={402}
							showGoDetails
							isScheduleScript={isScheduleScript}
						/>
					</Stack>
				</Fragment>
			}
		/>
	);
}
